import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import api from "./api";

export interface ActivePackage {
  key: string;
  name: string;
  hasQuotes: boolean;
  hasExpenses: boolean;
  hasPayments: boolean;
  hasKasboek?: boolean;
  maxInvoices: number | null;
  maxQuotes: number | null;
  maxExpenses: number | null;
  maxContacts: number | null;
  maxUsers?: number | null;
  freeLayoutTemplates?: number | null;
  hasOcr: boolean;
  ocrCreditsPerMonth?: number | null;
  hasVatReport: boolean;
  hasBankLink?: boolean;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  primaryColor?: string;
  role?: string;
  isPlatform?: boolean;
  activePackage?: ActivePackage | null;
}

export interface PackageFeatures {
  quotes: boolean;
  expenses: boolean;
  payments: boolean;
  kasboek: boolean;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  companies: Company[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (data: { email: string; password: string; firstName?: string; lastName?: string }) => Promise<User>;
  logout: () => void;
  refetchUser: () => Promise<void>;
  /** Id van het bedrijf waar momenteel in wordt gewerkt (X-Company-Id). */
  activeCompanyId: string | null;
  /** Bedrijf-object van het actieve bedrijf (naam/logo voor Topbar/Sidebar). */
  activeCompany: Company | null;
  /** Kies het actieve bedrijf. Superadmin mag élk bedrijf kiezen (geef het
   *  bedrijf-object mee); gewone gebruikers kiezen uit hun eigen bedrijven. */
  setCompany: (companyId: string, company?: Company) => void;
  /** Verlaat de bedrijfscontext (terug naar beheerpaneel voor superadmin). */
  clearCompany: () => void;
  /** Feature-vlaggen voor het actieve bedrijf (uit het pakket). Superadmin
   *  krijgt alles aan (alles mogen zien). */
  features: PackageFeatures;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readCachedActiveCompany(): Company | null {
  try {
    return JSON.parse(localStorage.getItem("aiboek_active_company") || "null");
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(
    () => localStorage.getItem("aiboek_company_id") || null
  );
  const [cachedCompany, setCachedCompany] = useState<Company | null>(readCachedActiveCompany);

  const fetchMe = async () => {
    try {
      const token = localStorage.getItem("aiboek_token");
      if (!token) {
        setLoading(false);
        return;
      }
      const { data } = await api.get<User>("/api/users/me");
      setUser(data);
      // Zorg dat er altijd een geldig actief bedrijf is. Als er nog geen
      // bedrijf gekozen is (of de opgeslagen id bestaat niet meer), pak dan
      // companies[0] — voor superadmin is dat het oudste/hoofdbedrijf
      // (bijv. AICORE BV), voor gewone gebruikers hun eerste bedrijf.
      const storedCompanyId = localStorage.getItem("aiboek_company_id");
      const hasCompany = data.companies.some((c) => c.id === storedCompanyId);
      if (data.companies.length && (!storedCompanyId || !hasCompany)) {
        localStorage.setItem("aiboek_company_id", data.companies[0].id);
        setActiveCompanyId(data.companies[0].id);
      }
    } catch {
      localStorage.removeItem("aiboek_token");
      localStorage.removeItem("aiboek_company_id");
      localStorage.removeItem("aiboek_active_company");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  // Het actieve bedrijf: voor gewone gebruikers uit user.companies, voor
  // superadmin uit de meegestuurde cache (superadmin kan elk bedrijf openen).
  const activeCompany: Company | null =
    user && activeCompanyId
      ? user.companies.find((c) => c.id === activeCompanyId) ||
        (cachedCompany && cachedCompany.id === activeCompanyId ? cachedCompany : null)
      : user && user.companies.length > 0 && user.role !== "SUPERADMIN"
        ? user.companies[0]
        : null;

  const setCompany = (companyId: string, company?: Company) => {
    localStorage.setItem("aiboek_company_id", companyId);
    setActiveCompanyId(companyId);
    if (company) {
      localStorage.setItem("aiboek_active_company", JSON.stringify(company));
      setCachedCompany(company);
    } else if (user) {
      const found = user.companies.find((c) => c.id === companyId);
      if (found) {
        localStorage.setItem("aiboek_active_company", JSON.stringify(found));
        setCachedCompany(found);
      }
    }
  };

  const clearCompany = () => {
    localStorage.removeItem("aiboek_company_id");
    localStorage.removeItem("aiboek_active_company");
    setActiveCompanyId(null);
    setCachedCompany(null);
  };

  // Feature-vlaggen uit het actieve pakket. De restrictie geldt voor iedereen
  // (ook superadmin) op basis van het actieve bedrijf: wie in H&H (Budget)
  // werkt, ziet alleen facturen — ongeacht de rol. Superadmin kan naar een
  // bedrijf met een vollediger pakket (bijv. AICORE, Basic) wisselen of het
  // beheerpaneel gebruiken. Een bedrijf zónder pakket = alles (legacy).
  const pkg = activeCompany?.activePackage;
  const features: PackageFeatures = {
    quotes: pkg?.hasQuotes ?? true,
    expenses: pkg?.hasExpenses ?? true,
    payments: pkg?.hasPayments ?? true,
    kasboek: pkg?.hasKasboek ?? true,
  };

  const login = async (email: string, password: string) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    localStorage.setItem("aiboek_token", data.accessToken);
    localStorage.setItem("aiboek_user_snapshot", JSON.stringify(data.user));
    // Iedereen — ook superadmin — start in het eerste/oudste bedrijf
    // (bijv. AICORE BV). Superadmin kan via de bedrijf-wisselaar wisselen
    // en via de sidebar naar het beheerpaneel.
    const first = data.user.companies[0]?.id || "";
    localStorage.setItem("aiboek_company_id", first);
    setActiveCompanyId(first || null);
    const c0 = data.user.companies[0];
    if (c0) {
      localStorage.setItem("aiboek_active_company", JSON.stringify(c0));
      setCachedCompany(c0);
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const { data } = await api.post("/api/auth/register", input);
    localStorage.setItem("aiboek_token", data.accessToken);
    localStorage.setItem("aiboek_company_id", data.user.companies[0]?.id || "");
    localStorage.setItem("aiboek_user_snapshot", JSON.stringify(data.user));
    setActiveCompanyId(data.user.companies[0]?.id || null);
    const c0 = data.user.companies[0];
    if (c0) {
      localStorage.setItem("aiboek_active_company", JSON.stringify(c0));
      setCachedCompany(c0);
    }
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("aiboek_token");
    localStorage.removeItem("aiboek_company_id");
    localStorage.removeItem("aiboek_user_snapshot");
    localStorage.removeItem("aiboek_active_company");
    setUser(null);
    setActiveCompanyId(null);
    setCachedCompany(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refetchUser: fetchMe,
        activeCompanyId,
        activeCompany,
        setCompany,
        clearCompany,
        features,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
